import React, { useState, useEffect } from 'react';
import { useAdmin } from '../../context/AdminContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Switch } from '../ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Loader2, Share2, Users, Trophy, Settings, Copy, Plus, Pencil, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

export const AdminReferrals = () => {
  const { apiCall } = useAdmin();
  const [referrals, setReferrals] = useState([]);
  const [partnerCodes, setPartnerCodes] = useState([]);
  const [stats, setStats] = useState(null);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isPartnerDialogOpen, setIsPartnerDialogOpen] = useState(false);
  const [editingPartner, setEditingPartner] = useState(null);
  const [partnerFormData, setPartnerFormData] = useState({
    code: '',
    partner_name: '',
    partner_email: '',
    discount_percent: 5,
    commission_percent: 5,
    is_active: true,
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [referralsData, statsData, settingsData, partnersData] = await Promise.all([
        apiCall('/admin/referrals'),
        apiCall('/admin/referrals/stats'),
        apiCall('/admin/referrals/settings'),
        apiCall('/admin/partner-codes').catch(() => []),
      ]);
      setReferrals(referralsData || []);
      setStats(statsData || {});
      setSettings(settingsData || {});
      setPartnerCodes(partnersData || []);
    } catch (err) {
      toast.error('Failed to fetch referral data');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      await apiCall('/admin/referrals/settings', {
        method: 'PUT',
        body: JSON.stringify(settings),
      });
      toast.success('Settings saved');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handlePartnerSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingPartner) {
        await apiCall(`/admin/partner-codes/${editingPartner.id}`, {
          method: 'PUT',
          body: JSON.stringify(partnerFormData),
        });
        toast.success('Partner code updated');
      } else {
        await apiCall('/admin/partner-codes', {
          method: 'POST',
          body: JSON.stringify(partnerFormData),
        });
        toast.success('Partner code created');
      }
      setIsPartnerDialogOpen(false);
      resetPartnerForm();
      fetchData();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleEditPartner = (partner) => {
    setEditingPartner(partner);
    setPartnerFormData({
      code: partner.code || '',
      partner_name: partner.partner_name || '',
      partner_email: partner.partner_email || '',
      discount_percent: partner.discount_percent || 5,
      commission_percent: partner.commission_percent || 5,
      is_active: partner.is_active ?? true,
    });
    setIsPartnerDialogOpen(true);
  };

  const handleDeletePartner = async (id) => {
    if (!window.confirm('Delete this partner code?')) return;
    try {
      await apiCall(`/admin/partner-codes/${id}`, { method: 'DELETE' });
      toast.success('Partner code deleted');
      fetchData();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const resetPartnerForm = () => {
    setEditingPartner(null);
    setPartnerFormData({
      code: '',
      partner_name: '',
      partner_email: '',
      discount_percent: 5,
      commission_percent: 5,
      is_active: true,
    });
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success('Code copied!');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-referrals">
      <div>
        <h2 className="text-2xl font-heading font-bold text-[#1A1625]">Referral Program</h2>
        <p className="text-muted-foreground">Manage customer referrals and rewards</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Referrals</p>
                <p className="text-3xl font-bold">{stats?.total_referrals || 0}</p>
              </div>
              <div className="p-3 rounded-full bg-purple-50">
                <Share2 className="h-6 w-6 text-primary" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Successful</p>
                <p className="text-3xl font-bold text-green-600">{stats?.successful_referrals || 0}</p>
              </div>
              <div className="p-3 rounded-full bg-green-50">
                <Trophy className="h-6 w-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Pending</p>
                <p className="text-3xl font-bold text-yellow-600">{stats?.pending_referrals || 0}</p>
              </div>
              <div className="p-3 rounded-full bg-yellow-50">
                <Users className="h-6 w-6 text-yellow-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="referrals">
        <TabsList>
          <TabsTrigger value="referrals" className="flex items-center gap-2">
            <Share2 className="h-4 w-4" /> Referrals
          </TabsTrigger>
          <TabsTrigger value="partner-codes" className="flex items-center gap-2">
            <Users className="h-4 w-4" /> Partner Codes
          </TabsTrigger>
          <TabsTrigger value="top-referrers" className="flex items-center gap-2">
            <Trophy className="h-4 w-4" /> Top Referrers
          </TabsTrigger>
          <TabsTrigger value="settings" className="flex items-center gap-2">
            <Settings className="h-4 w-4" /> Settings
          </TabsTrigger>
        </TabsList>

        <TabsContent value="referrals" className="space-y-4">
          <Card>
            <CardContent className="p-0">
              {referrals.length === 0 ? (
                <div className="p-8 text-center text-muted-foreground">No referrals yet</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-gray-50">
                        <th className="text-left py-3 px-4">Referrer</th>
                        <th className="text-left py-3 px-4">Referee Email</th>
                        <th className="text-left py-3 px-4">Code</th>
                        <th className="text-left py-3 px-4">Status</th>
                        <th className="text-left py-3 px-4">Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {referrals.map((ref) => (
                        <tr key={ref.id} className="border-b hover:bg-gray-50">
                          <td className="py-3 px-4 font-medium">{ref.referrer_id?.substring(0, 8)}...</td>
                          <td className="py-3 px-4">{ref.referee_email}</td>
                          <td className="py-3 px-4 font-mono text-xs">{ref.referral_code}</td>
                          <td className="py-3 px-4">
                            <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
                              ref.status === 'completed' ? 'bg-green-100 text-green-700' :
                              ref.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                              'bg-gray-100 text-gray-700'
                            }`}>
                              {ref.status}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-muted-foreground">
                            {new Date(ref.created_at).toLocaleDateString()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="partner-codes" className="space-y-4">
          <div className="flex justify-end">
            <Dialog open={isPartnerDialogOpen} onOpenChange={(open) => { setIsPartnerDialogOpen(open); if (!open) resetPartnerForm(); }}>
              <DialogTrigger asChild>
                <Button className="bg-primary"><Plus className="mr-2 h-4 w-4" /> Add Partner Code</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>{editingPartner ? 'Edit Partner Code' : 'Add Partner Code'}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handlePartnerSubmit} className="space-y-4">
                  <div>
                    <Label>Promo Code</Label>
                    <Input
                      value={partnerFormData.code}
                      onChange={(e) => setPartnerFormData({ ...partnerFormData, code: e.target.value.toUpperCase() })}
                      placeholder="e.g., PARTNER10"
                      required
                    />
                    <p className="text-xs text-muted-foreground mt-1">Customers will use this code at checkout</p>
                  </div>
                  <div>
                    <Label>Partner Name</Label>
                    <Input
                      value={partnerFormData.partner_name}
                      onChange={(e) => setPartnerFormData({ ...partnerFormData, partner_name: e.target.value })}
                      placeholder="Partner business or person name"
                      required
                    />
                  </div>
                  <div>
                    <Label>Partner Email</Label>
                    <Input
                      type="email"
                      value={partnerFormData.partner_email}
                      onChange={(e) => setPartnerFormData({ ...partnerFormData, partner_email: e.target.value })}
                      placeholder="partner@email.com"
                      required
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Customer Discount (%)</Label>
                      <Input
                        type="number"
                        min="0"
                        max="100"
                        value={partnerFormData.discount_percent}
                        onChange={(e) => setPartnerFormData({ ...partnerFormData, discount_percent: parseFloat(e.target.value) || 0 })}
                      />
                      <p className="text-xs text-muted-foreground mt-1">Discount for customers using this code</p>
                    </div>
                    <div>
                      <Label>Partner Commission (%)</Label>
                      <Input
                        type="number"
                        min="0"
                        max="100"
                        value={partnerFormData.commission_percent}
                        onChange={(e) => setPartnerFormData({ ...partnerFormData, commission_percent: parseFloat(e.target.value) || 0 })}
                      />
                      <p className="text-xs text-muted-foreground mt-1">Commission partner earns per sale</p>
                    </div>
                  </div>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={partnerFormData.is_active}
                      onChange={(e) => setPartnerFormData({ ...partnerFormData, is_active: e.target.checked })}
                      className="w-4 h-4"
                    />
                    <span>Active</span>
                  </label>
                  <div className="flex justify-end gap-2 pt-4">
                    <Button type="button" variant="outline" onClick={() => setIsPartnerDialogOpen(false)}>Cancel</Button>
                    <Button type="submit" className="bg-primary">{editingPartner ? 'Update' : 'Create'}</Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Partner / Affiliate Codes</CardTitle>
              <p className="text-sm text-muted-foreground">
                Create referral codes for partners. Customers get a discount, partners earn commission on each sale.
              </p>
            </CardHeader>
            <CardContent className="p-0">
              {partnerCodes.length === 0 ? (
                <div className="p-8 text-center text-muted-foreground">No partner codes yet. Create one to get started!</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-gray-50">
                        <th className="text-left py-3 px-4">Code</th>
                        <th className="text-left py-3 px-4">Partner</th>
                        <th className="text-left py-3 px-4">Discount</th>
                        <th className="text-left py-3 px-4">Commission</th>
                        <th className="text-left py-3 px-4">Uses</th>
                        <th className="text-left py-3 px-4">Earned</th>
                        <th className="text-left py-3 px-4">Status</th>
                        <th className="text-right py-3 px-4">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {partnerCodes.map((partner) => (
                        <tr key={partner.id} className="border-b hover:bg-gray-50">
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-2">
                              <span className="font-mono font-medium">{partner.code}</span>
                              <button onClick={() => copyCode(partner.code)} className="text-muted-foreground hover:text-primary">
                                <Copy className="h-3 w-3" />
                              </button>
                            </div>
                          </td>
                          <td className="py-3 px-4">
                            <div>
                              <p className="font-medium">{partner.partner_name}</p>
                              <p className="text-xs text-muted-foreground">{partner.partner_email}</p>
                            </div>
                          </td>
                          <td className="py-3 px-4 text-green-600 font-medium">{partner.discount_percent}%</td>
                          <td className="py-3 px-4 text-primary font-medium">{partner.commission_percent}%</td>
                          <td className="py-3 px-4">{partner.used_count || 0}</td>
                          <td className="py-3 px-4 font-medium">{(partner.total_commission || 0).toFixed(2)} RON</td>
                          <td className="py-3 px-4">
                            <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
                              partner.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                            }`}>
                              {partner.is_active ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-right">
                            <Button variant="ghost" size="sm" onClick={() => handleEditPartner(partner)}>
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button variant="ghost" size="sm" className="text-red-500" onClick={() => handleDeletePartner(partner.id)}>
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="top-referrers" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Top Referrers</CardTitle>
            </CardHeader>
            <CardContent>
              {stats?.top_referrers?.length > 0 ? (
                <div className="space-y-4">
                  {stats.top_referrers.map((referrer, index) => (
                    <div key={index} className="flex items-center justify-between p-4 border rounded-lg">
                      <div className="flex items-center gap-4">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-white ${
                          index === 0 ? 'bg-yellow-500' : index === 1 ? 'bg-gray-400' : index === 2 ? 'bg-amber-600' : 'bg-gray-300'
                        }`}>
                          {index + 1}
                        </div>
                        <div>
                          <p className="font-medium">{referrer.customer?.first_name} {referrer.customer?.last_name}</p>
                          <p className="text-sm text-muted-foreground">{referrer.customer?.email}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-bold text-primary">{referrer.referral_count}</p>
                        <p className="text-sm text-muted-foreground">referrals</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted-foreground text-center py-8">No referrals data yet</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="settings" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Referral Program Settings</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {settings && (
                <>
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div>
                      <p className="font-medium">Program Active</p>
                      <p className="text-sm text-muted-foreground">Enable or disable the referral program</p>
                    </div>
                    <Switch
                      checked={settings.is_active}
                      onCheckedChange={(checked) => setSettings({ ...settings, is_active: checked })}
                    />
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-4">
                      <h3 className="font-semibold">Referrer Reward (Person who refers)</h3>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Type</Label>
                          <select
                            className="w-full h-10 px-3 border rounded-lg"
                            value={settings.referrer_reward_type}
                            onChange={(e) => setSettings({ ...settings, referrer_reward_type: e.target.value })}
                          >
                            <option value="percent">Percentage</option>
                            <option value="fixed">Fixed Amount</option>
                          </select>
                        </div>
                        <div>
                          <Label>Value</Label>
                          <Input
                            type="number"
                            value={settings.referrer_reward_value}
                            onChange={(e) => setSettings({ ...settings, referrer_reward_value: parseFloat(e.target.value) || 0 })}
                          />
                        </div>
                      </div>
                    </div>

                    <div className="space-y-4">
                      <h3 className="font-semibold">Referee Reward (New customer)</h3>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Type</Label>
                          <select
                            className="w-full h-10 px-3 border rounded-lg"
                            value={settings.referee_reward_type}
                            onChange={(e) => setSettings({ ...settings, referee_reward_type: e.target.value })}
                          >
                            <option value="percent">Percentage</option>
                            <option value="fixed">Fixed Amount</option>
                          </select>
                        </div>
                        <div>
                          <Label>Value</Label>
                          <Input
                            type="number"
                            value={settings.referee_reward_value}
                            onChange={(e) => setSettings({ ...settings, referee_reward_value: parseFloat(e.target.value) || 0 })}
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div>
                    <Label>Minimum Order Value (RON)</Label>
                    <Input
                      type="number"
                      value={settings.min_order_value}
                      onChange={(e) => setSettings({ ...settings, min_order_value: parseFloat(e.target.value) || 0 })}
                      className="max-w-xs"
                    />
                    <p className="text-sm text-muted-foreground mt-1">Minimum order value for referral rewards to apply</p>
                  </div>

                  <Button onClick={handleSaveSettings} disabled={saving} className="bg-primary">
                    {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                    Save Settings
                  </Button>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminReferrals;
